import React, { useEffect, useState } from "react";
import {
    DKLabel,
    DKButton,
    DKInput,
    INPUT_VIEW_DIRECTION,
    DKCheckMark,
    removeLoader,
    showLoader,
    showToast,
    TOAST_TYPE,
} from "deskera-ui-library";
import {
    INPUT_TYPE,
    OBLIGATIONS,
    EMPLOYER_TYPES,
    EMPLOYER_TYPES_CATEGPORY_PUBLIC,
    EMPLOYER_TYPES_CATEGPORY_PRIVATE,
    EMPLOYER_TYPES_CATEGPORY_PROFESSIONAL,
    EPF_PAYMENT_TYPES,
    SECTOR_TYPES,
    PAYMENT_CODES,
} from "../constants/Constant";
import Utility, { deepClone } from "../utility/Utility";
import EmployeeListService from "../services/EmployeeListService";
import { getTenantCountryCode } from "../managers/common/CommonUtil";
import CompanyDetailService from "../services/CompanyDetailService";

const AddEpfAccount = (props) => {
    const formFields = [
        {
            columnCode: "epfAccountName",
            name: "EPF Account Name",
            isRequired: true,
            hasError: false,
            options: [],
            errMsg: "",
            hasTooltip: "",
            tooltipMsg: "",
            type: INPUT_TYPE.TEXT,
        },
        {
            columnCode: "obligations",
            name: "Obligation to Contribute to EPF",
            isRequired: true,
            hasError: false,
            options: OBLIGATIONS,
            errMsg: "",
            hasTooltip: "",
            tooltipMsg: "",
            type: INPUT_TYPE.DROPDOWN,
        },
        {
            columnCode: "employerType",
            name: "Employer Type",
            isRequired: true,
            hasError: false,
            options: EMPLOYER_TYPES,
            errMsg: "",
            hasTooltip: "",
            tooltipMsg: "",
            type: INPUT_TYPE.DROPDOWN,
        },
        {
            columnCode: "employerTypeCategory",
            name: "Employer Type Category",
            isRequired: true,
            hasError: false,
            options: [],
            errMsg: "",
            hasTooltip: "",
            tooltipMsg: "",
            type: INPUT_TYPE.DROPDOWN,
        },
        {
            columnCode: "epfCode",
            name: "EPF Code",
            isRequired: true,
            hasError: false,
            options: [],
            errMsg: "",
            hasTooltip: true,
            tooltipMsg:
                "EPF code is the activation code after you register for EPF'i-account'",
            type: INPUT_TYPE.TEXT,
        },
        {
            columnCode: "empEpf",
            name: "Employer EPF No",
            isRequired: true,
            hasError: false,
            options: [],
            errMsg: "",
            hasTooltip: "",
            tooltipMsg: "",
            type: INPUT_TYPE.TEXT,
        },
        {
            columnCode: "paymentType",
            name: "EPF Payment Type",
            isRequired: true,
            hasError: false,
            options: EPF_PAYMENT_TYPES,
            errMsg: "",
            hasTooltip: "",
            tooltipMsg: "",
            type: INPUT_TYPE.DROPDOWN,
        },
        {
            columnCode: "sequenceNo",
            name: "Sequence Number",
            isRequired: true,
            hasError: false,
            options: [],
            errMsg: "",
            hasTooltip: true,
            tooltipMsg:
                "The sequence number is used in the EPF output filename. If a value is present for the company for the month and the year, the sequence number is updated by 1. If no value is present, a sequence number of 0 is used.",
            type: INPUT_TYPE.NUMBER,
            hasMin: true,
            hasMax: true,
            minValue: 0,
            maxValue: 99,
        },
    ];

    const [cpfFormFields, setFormFields] = useState(formFields);
    const [defaultValues, setDefaultValues] = useState({
        includeSkillLevy: false,
        includeForeignLevy: false,
    });
    const [accountDetails, setAccountDetails] = useState(props.details);

    useEffect(() => {
        if (props.isEdit && !Utility.isEmptyObject(props.details)) {
            updateDefaultValues();
        }
    }, []);

    const getCategoryOptionsBasedEmployerType = (employerType) => {
        let options = [];
        if (employerType && employerType === "public") {
            options = EMPLOYER_TYPES_CATEGPORY_PUBLIC;
        } else if (employerType && employerType === "private") {
            options = EMPLOYER_TYPES_CATEGPORY_PRIVATE;
        } else if (employerType && employerType === "others") {
            options = EMPLOYER_TYPES_CATEGPORY_PROFESSIONAL;
        }
        return options;
    };

    const updateDefaultValues = () => {
        const complianceDetails = accountDetails?.compliance;
        const details = deepClone(defaultValues);
        details["epfAccountName"] = complianceDetails["epfAccountName"];
        details["obligations"] = EmployeeListService.getValueObjectFromDropdown(
            complianceDetails["obligations"],
            OBLIGATIONS,
            "id"
        );
        details["employerType"] = EmployeeListService.getValueObjectFromDropdown(
            complianceDetails["employerType"],
            EMPLOYER_TYPES,
            "id"
        );
        details["employerTypeCategory"] =
            EmployeeListService.getValueObjectFromDropdown(
                complianceDetails["employerTypeCategory"],
                getCategoryOptionsBasedEmployerType(complianceDetails["employerType"]),
                "id"
            );
        details["epfCode"] = complianceDetails["epfCode"];
        details["empEpf"] = complianceDetails["empEpf"];
        details["paymentType"] = EmployeeListService.getValueObjectFromDropdown(
            complianceDetails["paymentType"],
            EPF_PAYMENT_TYPES,
            "id"
        );
        details["sequenceNo"] = complianceDetails["sequenceNo"];
        details["accountId"] = accountDetails?.accountId;
        setDefaultValues(details);
    };

    const onSave = () => {
        if (isInvalid()) {
            return;
        }
        const parameters = getAccountObject();
        if (!props.isEdit) {
            saveDetails(parameters);
        } else {
            updateDetails(parameters);
        }
    };

    const getAccountObject = () => {
        const params = {
            countryCode: getTenantCountryCode(),
            accountType: "EPF",
            compliance: {
                "@type": "type.googleapis.com/v1.EPFAccount",
                countryCode: getTenantCountryCode(),
                epfAccountName: defaultValues["epfAccountName"],
                obligations: defaultValues["obligations"]?.id,
                employerType: defaultValues["employerType"]?.id,
                employerTypeCategory: defaultValues["employerTypeCategory"]?.id,
                epfCode: !Utility.isEmpty(defaultValues["epfCode"])
                    ? defaultValues["epfCode"]
                    : "",
                empEpf: !Utility.isEmpty(defaultValues["empEpf"])
                    ? defaultValues["empEpf"]
                    : "",
                paymentType: defaultValues["paymentType"]?.id,
                sequenceNo: defaultValues["sequenceNo"],
            },
        };
        if (props.isEdit) {
            params["accountId"] = defaultValues["accountId"];
        }
        return params;
    };

    const saveDetails = (params) => {
        showLoader("Adding... Please wait");
        CompanyDetailService.addCPFAccount(params).then(
            (response) => {
                showToast("EPF Account added successfully", TOAST_TYPE.SUCCESS);
                removeLoader();
                props.onClose(true);
            },
            (error) => {
                showToast("Something went wrong.", TOAST_TYPE.FAILURE);
                removeLoader();
                props.onClose(false);
            }
        );
    };

    const updateDetails = (params) => {
        showLoader("Updating... Please wait");
        CompanyDetailService.updateCPFAccount(params).then(
            (response) => {
                showToast("EPF Account updated successfully", TOAST_TYPE.SUCCESS);
                removeLoader();
                props.onClose(true);
            },
            (error) => {
                showToast("Something went wrong.", TOAST_TYPE.FAILURE);
                removeLoader();
                props.onClose(false);
            }
        );
    };

    const isInvalid = () => {
        const alteredConfig = [];
        const formFieldsCopy = deepClone(cpfFormFields);
        for (let i = 0; i < formFieldsCopy.length; i++) {
            let field = formFieldsCopy[i];
            if (
                (field.isRequired &&
                    Utility.isEmpty(defaultValues[field.columnCode])) ||
                field.hasError
            ) {
                field.hasError = true;
                alteredConfig.push(field);
            }
        }
        if (alteredConfig.length > 0) {
            const formfields = EmployeeListService.getAlteredConfigDetails(
                formFieldsCopy,
                alteredConfig,
                false,
                {},
                {}
            );
            setFormFields(formfields);
        }
        return alteredConfig.length > 0;
    };

    const onCheckBoxClick = (field, index) => {
        const values = deepClone(defaultValues);
        values[field.columnCode] = !values[field.columnCode];
        setDefaultValues(values);
    };

    const onChange = (value, field, index) => {
        const formFieldsCopy = deepClone(cpfFormFields);
        const alteredConfig = [];
        switch (field.columnCode) {
            case "employerType":
                let options = getCategoryOptionsBasedEmployerType(value.id);
                alteredConfig.push({
                    columnCode: "employerTypeCategory",
                    options: options,
                    hasError: true,
                });
                defaultValues["employerTypeCategory"] = {};
                if (field.isRequired && Utility.isEmpty(value)) {
                    field.hasError = true;
                    field.errMsg = `Enter a valid ${field.name}`;
                } else {
                    field.hasError = false;
                }
                break;
            default:
                switch (field.type) {
                    case INPUT_TYPE.NUMBER:
                        const intValue = parseInt(value);
                        if (field.isRequired && Utility.isEmpty(intValue)) {
                            field.hasError = true;
                            field.errMsg = `Enter a valid ${field.name}`;
                        } else if (field.hasMin && intValue < field.minValue) {
                            field.hasError = true;
                            field.errMsg = "Enter a valid number";
                        } else if (field.hasMax && intValue > field.maxValue) {
                            field.hasError = true;
                            field.errMsg = "Enter a valid number";
                        } else {
                            field.hasError = false;
                        }
                        break;
                    default:
                        if (field.isRequired && Utility.isEmpty(value)) {
                            field.hasError = true;
                            field.errMsg = `Enter a valid ${field.name}`;
                        } else {
                            field.hasError = false;
                        }
                }
        }
        alteredConfig.push(field);
        defaultValues[field.columnCode] = value;
        const formfields = EmployeeListService.getAlteredConfigDetails(
            formFieldsCopy,
            alteredConfig,
            false,
            {},
            {}
        );
        setFormFields(formfields);
    };

    const getInputForm = () => {
        return cpfFormFields.map((field, index) => {
            return (
                <>
                    {field.type != INPUT_TYPE.CHECKBOX && (
                        <div className="column parent-width mb-l">
                            <DKInput
                                title={field.name}
                                type={field.type}
                                formatter={(obj) => {
                                    /** How to render a selected option in case of object **/
                                    return obj.name;
                                }}
                                value={defaultValues[field.columnCode]}
                                direction={INPUT_VIEW_DIRECTION.VERTICAL}
                                required={field.isRequired}
                                canValidate={field.hasError}
                                errorMessage={field.errMsg}
                                validator={(value) => {
                                    return !field.hasError;
                                }}
                                onChange={(value) => {
                                    onChange(value, field, index);
                                }}
                                tooltip={{
                                    content: field.tooltipMsg,
                                    className: "warning-tooltip-prop",
                                }}
                                dropdownConfig={{
                                    className: "",
                                    style: {},
                                    allowSearch:
                                        field.options && field.options.length > 6 ? true : false,
                                    searchableKey: "name",
                                    data: field.options,
                                    renderer: (index, obj) => {
                                        /** How to render value in Dropdown in case of object **/
                                        return <DKLabel text={`${obj.name}`} />;
                                    },
                                    onSelect: (index, obj) => {
                                        // onValueChange(obj, "Quarter");
                                    },
                                }}
                            />
                        </div>
                    )}
                    {field.type === INPUT_TYPE.CHECKBOX && (
                        <div className="row mb-m" key={field.columnCode}>
                            <div onClick={() => onCheckBoxClick(field, index)}>
                                <DKCheckMark
                                    isSelected={defaultValues[field.columnCode]}
                                    color="bg-app"
                                />
                            </div>
                            <DKLabel className="ml-s" text={field.name} />
                        </div>
                    )}
                </>
            );
        });
    };

    const getHeader = () => {
        return (
            <div className="row justify-content-between p-h-m">
                <DKLabel
                    text={!props.isEdit ? "Add EPF Account" : "Edit EPF Account"}
                    className="fw-b fs-m"
                />
                <div className="row width-auto">
                    <DKButton
                        title="Cancel"
                        className="bg-gray1 border-m"
                        onClick={() => {
                            props.onClose(false);
                        }}
                    />
                    <DKButton
                        title="Save"
                        className="bg-button text-white ml-r"
                        onClick={() => {
                            onSave();
                        }}
                    />
                </div>
            </div>
        );
    };

    return (
        <div className=" transparent-background-light">
            <div className="p-h-r pt-r pb-xl popup-window">
                <div className="popup-wrapper parent-width parent-height">
                    {getHeader()}
                    <div className="p-m">{getInputForm()}</div>
                </div>
            </div>
        </div>
    );
};

export default AddEpfAccount;
